import { Link } from "wouter";
import { ReactNode, AnchorHTMLAttributes } from "react";

/**
 * Routes that should use client-side navigation (Wouter).
 * All other routes will use regular <a> tags for full page navigation.
 *
 * Note: "/" is NOT included here - navigation to root should always
 * do a full page reload so isPublicRoute() can re-evaluate auth state.
 */
const CLIENT_ROUTES = ["/docs", "/blog"];

/**
 * Check if a path should use client-side routing.
 * Returns true for marketing pages (/, /docs, /blog, /blog/*).
 */
function isClientRoute(href: string): boolean {
  // External links
  if (href.startsWith("http://") || href.startsWith("https://")) {
    return false;
  }

  // Hash links (authenticated app uses hash routing)
  if (href.startsWith("#")) {
    return false;
  }

  // When the authenticated hash app is active (e.g. /#app), PublicRouter
  // is not mounted — Wouter links would change the URL without rendering
  // anything.  Fall back to regular <a> so the browser does a full navigation.
  if (typeof window !== "undefined" && window.location.hash) {
    return false;
  }

  // Exact matches
  if (CLIENT_ROUTES.includes(href)) {
    return true;
  }

  // Blog posts and category pages (/blog/*)
  if (href.startsWith("/blog/")) {
    return true;
  }

  return false;
}

interface SiteLinkProps extends Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  "href"
> {
  href: string;
  children: ReactNode;
  className?: string;
}

/**
 * SiteLink - A smart link component for the marketing site.
 *
 * Uses Wouter's <Link> for client-side navigation on marketing pages
 * (instant navigation without page reload), and falls back to regular
 * <a> tags for external links and authenticated app routes.
 *
 * Usage:
 * ```tsx
 * <SiteLink href="/docs">Documentation</SiteLink>
 * <SiteLink href="/blog/my-post">Read more</SiteLink>
 * <SiteLink href="https://github.com">GitHub</SiteLink>
 * ```
 */
export function SiteLink({
  href,
  children,
  className,
  ...props
}: SiteLinkProps) {
  if (isClientRoute(href)) {
    return (
      <Link href={href} className={className} {...props}>
        {children}
      </Link>
    );
  }

  // External links or non-client routes - use regular anchor
  return (
    <a href={href} className={className} {...props}>
      {children}
    </a>
  );
}

export default SiteLink;
